import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import './Loans.css';

const Loans = () => {
    const { t } = useLanguage();
    const { hasPermission } = useAuth();
    const navigate = useNavigate();
    const canApprove = hasPermission('loans.approve');
    const canDisburse = hasPermission('loans.disburse');
    const [loans, setLoans] = useState([]);
    const [pendingLoans, setPendingLoans] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        member_id: '',
        loan_type_id: '',
        principal_amount: '',
        tenure_months: '',
        interest_rate: '5',
        purpose: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // getPendingLoans() needs loans.approve (treasurer/admin only) — members
            // and other loans.read-only roles would 403 on it, and since it used to
            // sit in the same Promise.all as the other calls, that one 403 was
            // failing the whole load and leaving the page blank for everyone else.
            const [allLoans, pending, loanSummary] = await Promise.all([
                api.getLoans(),
                canApprove ? api.getPendingLoans() : Promise.resolve({ data: [] }),
                api.getLoansSummary()
            ]);
            setLoans(allLoans.data);
            setPendingLoans(pending.data);
            setSummary(loanSummary.data);
        } catch (error) {
            console.error('Error loading loans:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-TZ', {
            style: 'currency',
            currency: 'TZS',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            pending: 'badge-warning',
            under_review: 'badge-info',
            approved: 'badge-success',
            rejected: 'badge-danger',
            active: 'badge-success',
            completed: 'badge-info',
            defaulted: 'badge-danger',
            cancelled: 'badge-secondary'
        };
        return statusMap[status] || 'badge-secondary';
    };

    const handleApprove = async (id) => {
        if (window.confirm(t('Approve this loan application?'))) {
            try {
                await api.approveLoan(id, { notes: 'Approved' });
                loadData();
            } catch (error) {
                console.error('Error approving loan:', error);
            }
        }
    };

    const handleReject = async (id) => {
        if (window.confirm(t('Reject this loan application?'))) {
            try {
                await api.rejectLoan(id, { notes: 'Rejected' });
                loadData();
            } catch (error) {
                console.error('Error rejecting loan:', error);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.createLoan(formData);
            setShowModal(false);
            setFormData({
                member_id: '',
                loan_type_id: '',
                principal_amount: '',
                tenure_months: '',
                interest_rate: '5',
                purpose: ''
            });
            loadData();
        } catch (error) {
            console.error('Error creating loan:', error);
        }
    };

    return (
        <div className="loans-page">
            <div className="page-header">
                <button type="button" className="loans-back" aria-label={t('Back')} onClick={() => navigate('/')}>←</button>
                <div className="loans-heading">
                    <span className="loans-eyebrow">TAREWU FINANCE</span>
                    <h1 className="page-title">{t('Loans Management')}</h1>
                    <p>{t('Applications, approvals, disbursements and balances')}</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                    + {t('New Loan Application')}
                </button>
            </div>

            {/* Summary Stats */}
            {summary && (
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon">🏦</div>
                        <div className="stat-value">{summary.totals?.total_loans || 0}</div>
                        <div className="stat-label">{t('Total Loans')}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">💰</div>
                        <div className="stat-value">{formatCurrency(summary.totals?.total_disbursed)}</div>
                        <div className="stat-label">{t('Total Disbursed')}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">📊</div>
                        <div className="stat-value">{formatCurrency(summary.totals?.total_outstanding)}</div>
                        <div className="stat-label">{t('Outstanding')}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">⏳</div>
                        <div className="stat-value">{pendingLoans.length}</div>
                        <div className="stat-label">{t('Pending Review')}</div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="tabs">
                <button
                    className={`tab ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                >
                    {t('All Loans')}
                </button>
                {canApprove && (
                    <button
                        className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
                        onClick={() => setActiveTab('pending')}
                    >
                        {t('Pending')} ({pendingLoans.length})
                    </button>
                )}
            </div>

            {/* Loans Table */}
            <div className="card">
                {loading ? (
                    <div className="loading">
                        <div className="loading-spinner"></div>
                    </div>
                ) : (
                    <>
                    <div className="loan-mobile-list">
                        {(activeTab === 'pending' ? pendingLoans : loans).map(loan => <article className="loan-mobile-card" key={`mobile-${loan.id}`}>
                            <div className="loan-mobile-head"><div><small>{loan.loan_number || `Loan #${loan.id}`}</small><h4>{loan.first_name} {loan.last_name}</h4><span>{loan.member_number}</span></div><span className={`badge ${getStatusBadge(loan.status)}`}>{loan.status}</span></div>
                            <div className="loan-amount-panel"><div><small>{t('Principal')}</small><strong>{formatCurrency(loan.principal_amount)}</strong></div><div><small>{t('Balance')}</small><strong>{formatCurrency(loan.balance)}</strong></div></div>
                            <div className="loan-mobile-details"><span>{t('Interest')} <b>{loan.interest_rate}%</b></span>{loan.tenure_months&&<span>{t('Tenure')} <b>{loan.tenure_months} {t('months')}</b></span>}</div>
                            {activeTab === 'pending'&&<div className="loan-mobile-actions"><button className="btn btn-sm btn-success" onClick={()=>handleApprove(loan.id)}>{t('Approve')}</button><button className="btn btn-sm btn-danger" onClick={()=>handleReject(loan.id)}>{t('Reject')}</button></div>}
                            {canDisburse&&activeTab==='all'&&loan.status==='approved'&&<div className="loan-mobile-actions"><button className="btn btn-sm btn-primary" onClick={async()=>{try{await api.disburseLoan(loan.id);loadData();}catch(error){console.error('Error disbursing loan:',error);}}}>{t('Disburse')}</button></div>}
                        </article>)}
                        {(activeTab === 'pending' ? pendingLoans : loans).length===0&&<div className="loan-empty"><span>🏦</span><h3>{t('No loans found')}</h3><p>{t('Loan records will appear here.')}</p></div>}
                    </div>
                    <div className="table-container loan-desktop-table">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>{t('Loan #')}</th>
                                    <th>{t('Member')}</th>
                                    <th>{t('Amount')}</th>
                                    <th>{t('Interest')}</th>
                                    <th>{t('Balance')}</th>
                                    <th>{t('Status')}</th>
                                    <th>{t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(activeTab === 'pending' ? pendingLoans : loans).map((loan) => (
                                    <tr key={loan.id}>
                                        <td>{loan.loan_number}</td>
                                        <td>
                                            {loan.first_name} {loan.last_name}
                                            <div className="member-number">{loan.member_number}</div>
                                        </td>
                                        <td>{formatCurrency(loan.principal_amount)}</td>
                                        <td>{loan.interest_rate}%</td>
                                        <td>{formatCurrency(loan.balance)}</td>
                                        <td>
                                            <span className={`badge ${getStatusBadge(loan.status)}`}>
                                                {loan.status}
                                            </span>
                                        </td>
                                        <td>
                                            {activeTab === 'pending' && (
                                                <div className="action-buttons">
                                                    <button
                                                        className="btn btn-sm btn-success"
                                                        onClick={() => handleApprove(loan.id)}
                                                    >
                                                        {t('Approve')}
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleReject(loan.id)}
                                                    >
                                                        {t('Reject')}
                                                    </button>
                                                </div>
                                            )}
                                            {canDisburse && activeTab === 'all' && loan.status === 'approved' && (
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={async () => {
                                                        try {
                                                            await api.disburseLoan(loan.id);
                                                            loadData();
                                                        } catch (error) {
                                                            console.error('Error disbursing loan:', error);
                                                        }
                                                    }}
                                                >
                                                    {t('Disburse')}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div></>
                )}
            </div>

            {/* New Loan Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">{t('New Loan Application')}</h3>
                            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleSubmit} className="loan-application-form">
                            <div className="modal-body">
                                <div className="loan-form-intro"><span>🏦</span><div><b>{t('Loan request details')}</b><p>{t('Enter the requested amount, repayment period and purpose.')}</p></div></div>
                                <h4 className="loan-form-section">{t('Applicant')}</h4>
                                <div className="form-group">
                                    <label className="form-label">{t('Member ID')} *</label>
                                    <input
                                        type="number"
                                        name="member_id"
                                        className="form-input"
                                        value={formData.member_id}
                                        onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
                                        min="1"
                                        placeholder={t('Member system ID')}
                                        required
                                    />
                                    <small className="loan-field-help">{t('Members submitting their own request are linked automatically.')}</small>
                                </div>
                                <h4 className="loan-form-section">{t('Loan details')}</h4>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('Amount (TZS)')} *</label>
                                        <input
                                            type="number"
                                            name="principal_amount"
                                            className="form-input"
                                            value={formData.principal_amount}
                                            onChange={(e) => setFormData({ ...formData, principal_amount: e.target.value })}
                                            min="1"
                                            step="1000"
                                            placeholder="0"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('Tenure (Months)')} *</label>
                                        <input
                                            type="number"
                                            name="tenure_months"
                                            className="form-input"
                                            value={formData.tenure_months}
                                            onChange={(e) => setFormData({ ...formData, tenure_months: e.target.value })}
                                            min="1"
                                            max="60"
                                            placeholder="12"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('Interest Rate (%)')}</label>
                                    <input
                                        type="number"
                                        name="interest_rate"
                                        className="form-input"
                                        value={formData.interest_rate}
                                        onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                                        step="0.5"
                                        min="0"
                                        max="100"
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('Purpose')}</label>
                                    <textarea
                                        name="purpose"
                                        className="form-input"
                                        value={formData.purpose}
                                        onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                                        rows="3"
                                        placeholder={t('Explain briefly how the loan will be used')}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    {t('Cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {t('Submit Application')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Loans;
